import { absUrl } from "@/lib/url";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PdfExportButton } from "@/components/PdfExportButton";
import { AdSlot } from "@/components/AdSlot";
import {
  HIJRI_MONTHS,
  HIJRI_MONTHS_EN,
  hijriToGregorian,
  daysInHijriMonth,
} from "@/lib/calendar/hijri";
import { getHijriEvent } from "@/lib/calendar/hijri-events";
import { getDayInfo } from "@/lib/calendar/info";
import { calendarDatePath, calendarMonthPath } from "@/lib/calendar/dates";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import type { Locale } from "@/i18n/types";
import { eventSchema } from "@/lib/seo/schema";

export function HijriDatePage({
  hYear,
  hMonth,
  hDay,
  locale,
}: {
  hYear: number;
  hMonth: number;
  hDay: number;
  locale?: Locale;
}) {
  return (
    <SiteShell locale={locale}>
      <Body hYear={hYear} hMonth={hMonth} hDay={hDay} />
    </SiteShell>
  );
}

function Body({ hYear, hMonth, hDay }: { hYear: number; hMonth: number; hDay: number }) {
  const { t, href, locale } = useLocale();
  const monthName = (locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[hMonth - 1];
  const total = daysInHijriMonth(hYear, hMonth);
  const safeDay = Math.min(Math.max(1, hDay), total);
  const g = hijriToGregorian(hYear, hMonth, safeDay);
  const info = getDayInfo(g.y, g.m, g.d);
  const ev = getHijriEvent(hMonth, safeDay);
  const evName = ev ? (locale === "en" ? ev.nameEn : ev.name) : null;
  const evDesc = ev ? (locale === "en" ? ev.descEn : ev.descId) : null;

  // Prev / next Hijri day
  const prev = safeDay > 1
    ? { hy: hYear, hm: hMonth, hd: safeDay - 1 }
    : hMonth > 1
      ? { hy: hYear, hm: hMonth - 1, hd: daysInHijriMonth(hYear, hMonth - 1) }
      : { hy: hYear - 1, hm: 12, hd: daysInHijriMonth(hYear - 1, 12) };
  const next = safeDay < total
    ? { hy: hYear, hm: hMonth, hd: safeDay + 1 }
    : hMonth < 12
      ? { hy: hYear, hm: hMonth + 1, hd: 1 }
      : { hy: hYear + 1, hm: 1, hd: 1 };

  const status = info.isLibur
    ? locale === "en" ? "Public Holiday" : "Libur Nasional"
    : ev?.type === "hari-besar"
      ? locale === "en" ? "Major Islamic Day" : "Hari Besar Islam"
      : ev?.type === "puasa"
        ? locale === "en" ? "Recommended Fasting" : "Puasa Sunnah"
        : info.isCutiBersama
          ? locale === "en" ? "Joint Leave" : "Cuti Bersama"
          : locale === "en" ? "Regular day" : "Hari biasa";
  const statusClass = info.isLibur
    ? "bg-red-date/10 text-red-date"
    : ev?.type === "hari-besar"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : ev?.type === "puasa"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : info.isCutiBersama
          ? "bg-cuti-bersama/10 text-cuti-bersama"
          : "bg-muted text-muted-foreground";

  const title = `${safeDay} ${monthName} ${hYear} ${locale === "en" ? "AH" : "H"}`;
  const gregorianLong = `${info.dayName === "Sunday" || info.dayName === "Minggu" ? info.dayName : info.dayName}, ${g.d} ${t.common.months[g.m - 1]} ${g.y}`;

  const ld: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t.common.home, item: absUrl(href("/")) },
        { "@type": "ListItem", position: 2, name: t.common.hijriCalendar, item: absUrl(href("/kalender-hijriah/")) },
        { "@type": "ListItem", position: 3, name: `${hYear} H`, item: absUrl(href(`/kalender-hijriah/${hYear}/`)) },
        { "@type": "ListItem", position: 4, name: `${monthName} ${hYear}`, item: absUrl(href(`/kalender-hijriah/${hYear}/${hMonth}/`)) },
        { "@type": "ListItem", position: 5, name: title, item: absUrl(href(`/kalender-hijriah/${hYear}/${hMonth}/${safeDay}/`)) },
      ],
    },
  ];
  if (ev) {
    ld.push(
      eventSchema(
        {
          name: evName ?? title,
          date: info.iso,
          description: evDesc ?? undefined,
          url: `/kalender-hijriah/${hYear}/${hMonth}/${safeDay}/`,
        },
        locale === "en" ? "en" : "id",
      ),
    );
  }

  return (
    <>
      {ld.map((node, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(node) }} />
      ))}
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">{t.common.home}</Link>
        {" / "}
        <Link to={href("/kalender-hijriah/") as never} className="hover:text-primary">{t.common.hijriCalendar}</Link>
        {" / "}
        <Link to={href(`/kalender-hijriah/${hYear}/`) as never} className="hover:text-primary">{hYear} H</Link>
        {" / "}
        <Link to={href(`/kalender-hijriah/${hYear}/${hMonth}/`) as never} className="hover:text-primary">{monthName}</Link>
        {" / "}{safeDay}
      </nav>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className={`font-display text-4xl tracking-tight md:text-5xl ${info.isLibur ? "text-red-date" : ""}`}>
            {title}
          </h1>
          <p className="mt-1 text-muted-foreground">{gregorianLong}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={`rounded-md px-2.5 py-1 text-xs font-medium ${statusClass}`}>{status}</span>
            {locale === "id" && (
              <span className="rounded-md bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                Pasaran: <span className="font-medium text-foreground">{info.pasaran}</span>
              </span>
            )}
          </div>
        </div>
        <PdfExportButton mode="h-day" year={g.y} hYear={hYear} hMonth={hMonth} hDay={safeDay} allowSwitch />
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-3 font-semibold">{locale === "en" ? "Date conversions" : "Penanggalan"}</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{locale === "en" ? "Hijri" : "Hijriah"}</dt>
              <dd className="font-medium">{title}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{locale === "en" ? "Gregorian" : "Masehi"}</dt>
              <dd className="font-medium">
                <Link to={href(calendarDatePath(g.y, g.m, g.d)) as never} className="text-primary hover:underline">
                  {g.d} {t.common.months[g.m - 1]} {g.y}
                </Link>
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{locale === "en" ? "Day" : "Hari"}</dt>
              <dd className="font-medium">{info.dayName}</dd>
            </div>
            {locale === "id" && (
              <>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Pasaran</dt>
                  <dd className="font-medium">{info.pasaran}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Weton</dt>
                  <dd className="font-medium">{info.dayName} {info.pasaran}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Jawa</dt>
                  <dd className="font-medium">{info.jawa.tanggalJawa} {info.jawa.bulanJawa} {info.jawa.tahunJawa}</dd>
                </div>
              </>
            )}
          </dl>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-3 font-semibold">{locale === "en" ? "Notable events" : "Hari Penting & Libur"}</h2>
          {!ev && info.holidays.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {locale === "en"
                ? "No notable Islamic event or holiday on this date."
                : "Tidak ada hari penting Islam atau libur pada tanggal ini."}
            </p>
          ) : (
            <ul className="space-y-3 text-sm">
              {ev && (
                <li>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${ev.type === "hari-besar" ? "bg-emerald-500" : ev.type === "puasa" ? "bg-amber-500" : "bg-muted-foreground"}`} />
                    <span className="font-medium">{evName}</span>
                  </div>
                  {evDesc && <p className="mt-1 text-xs text-muted-foreground">{evDesc}</p>}
                </li>
              )}
              {info.holidays.map((h, i) => (
                <li key={i}>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${h.type === "libur" ? "bg-red-date" : "bg-cuti-bersama"}`} />
                    <span className={`font-medium ${h.type === "libur" ? "text-red-date" : "text-cuti-bersama"}`}>
                      {translateHolidayName(h.name, locale)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {h.type === "libur"
                      ? locale === "en" ? "Public holiday" : "Libur nasional"
                      : locale === "en" ? "Joint leave" : "Cuti bersama"}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Link
          to={href(`/kalender-hijriah/${prev.hy}/${prev.hm}/${prev.hd}/`) as never}
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
        >
          ← {prev.hd} {(locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[prev.hm - 1]}
        </Link>
        <Link
          to={href(`/kalender-hijriah/${hYear}/${hMonth}/`) as never}
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
        >
          {monthName} {hYear} H
        </Link>
        <Link
          to={href(`/kalender-hijriah/${next.hy}/${next.hm}/${next.hd}/`) as never}
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
        >
          {next.hd} {(locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[next.hm - 1]} →
        </Link>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 text-xs">
        <Link
          to={href(calendarMonthPath(g.y, g.m)) as never}
          className="text-primary hover:underline"
        >
          {locale === "en" ? "View Gregorian month" : "Lihat bulan Masehi"}: {t.common.months[g.m - 1]} {g.y}
        </Link>
      </div>

      <AdSlot size="rectangle" position="sidebar" />
    </>
  );
}